import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import './ShippingDetailsModal.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { getAuctionLotUrl } from '../utilities/getSEOFriendlyURL.js';
import { isProd } from '../../../../utilities/UrlPaths.js';

import {
  Button,
  Col,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from 'reactstrap';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { createShippingReviewPayloadForUpdate } from '../utilities/updateReviewPayload';
import EditItemShippingDetails from './EditItemShippingDetails';
import { fieldsToCompareForConfidenceLevel } from '../../../../constants';
const requiredObjectLevelFields = ['width', 'height', 'depth', 'title', 'artaSubtype'];

const ShippingDetailsModal = ({ isOpen, close, lotData, onSave, setShowDeleteModal, setDeletionConfirmed, deletionConfirmed }) => {
  const policyTerms = useSelector((state) => state.policy.terms);
  const fulfillmentOrigins = useSelector((state) => state.policy.origins);
  const artaSubtypeList = useSelector((state) => state.shippingReview.artaSubtypeList);
  const [multiItemFlag, setMultiItemFlag] = useState(false);
  const [formData, setFormData] = useState({
    originID: '',
    termsID: '',
    items: [],
  });
  const validationErrors = lotData?.shippingReviewData?.quotesSampling?.validationErrors?.objects;
  const [isInvalid, setIsInvalid] = useState({});
  const [hasError, setHasError] = useState(false);
  const newObjectsCountRef = useRef(0);
  const [indexOfLotToDelete, setIndexOfLotToDelete] = useState(null);
  const [pdpUrl, setPDPUrl] = useState('');
  const [expandedAccordions, setExpandedAccordions] = useState(new Set());
  const [confidenceLevel, setConfidenceLevel] = useState('');
  const [warningMessage, setWarningMessage] = useState({});
  const [confidenceLevelPerField, setConfidenceLevelPerField] = useState(lotData?.shippingReviewData?.confidenceLevels);

  // Only 5 fields (width, height, depth, isFragile, title) need validation the rest have default values
  const validateSingleValue = useCallback((id, name, value) => {
    if (name.startsWith('width') || name.startsWith('height')) {
      if (!value || Number.isNaN(Number(value)) || Number(value) <= 0) {
        setIsInvalid((prev) => ({
          ...prev,
          [id]: {
            ...prev[id],
            [name]: true,
          }
        }));
        return;
      } else {
        setIsInvalid((prev) => ({
          ...prev,
          [id]: {
            ...prev[id],
            [name]: false
          }
        }));
        return;
      }
    } else if (name.startsWith('depth') ) {
      if (!value || Number.isNaN(Number(value)) || Number(value) < 0) {
        setIsInvalid((prev) => ({
          ...prev,
          [id]: {
            ...prev[id],
            [name]: true,
          }
        }));
        return;
      } else {
        setIsInvalid((prev) => ({
          ...prev,
          [id]: {
            ...prev[id],
            [name]: false
          }
        }));
        return;
      }
    }
    else if (!value) {
      setIsInvalid((prev) => ({
        ...prev,
        [id]: {
            ...prev[id],
            [name]: true
          }
      }));
      return;
    } else {
      setIsInvalid((prev) => ({
        ...prev,
        [id]: {
            ...prev[id],
            [name]: false
          }
      }));
      return;
    }
  }, []);

  const validateAllValues = useCallback(() => {
    formData.items.forEach((item) => {
      requiredObjectLevelFields.forEach((field) => {
        validateSingleValue(item.id, field, item[field]);
      });
    });
  }, [formData.items, validateSingleValue]);

  const deleteObject = useCallback(() => {
    let newItems = [...formData.items];
    const deletedItemArr = newItems.splice(indexOfLotToDelete, 1);
    const idToDelete = deletedItemArr[0].id;
    setIsInvalid((prev) => {
      const {[idToDelete]: deleted, ...rest} = prev;
      return rest;
    });
    setExpandedAccordions(prev => {
      const newSet = new Set(prev);
      newSet.delete(idToDelete);
      return newSet;
    });
    setFormData({
      ...formData,
      items: newItems
    });
  }, [formData, indexOfLotToDelete, setIsInvalid, setExpandedAccordions, setFormData]);

  // Check for BE validation errors and set initial error state
  useEffect(() => {
    if (!validationErrors || validationErrors?.length === 0) return;
    let initialErrors = validationErrors.reduce((acc, error) => {
      return acc = {
        ...acc,
        [error.objectID]: {
          ...error.errors.reduce((acc, item) => {
            return {
              ...acc,
              [item.field]: true
            }
          }, {})
        }
      }
    }, {});
    setIsInvalid(initialErrors);
  }, [validationErrors]);

  useEffect(() => {
    // construct review data
    if (!lotData || !lotData?.shippingReviewData) return;
    const reviewData = lotData?.shippingReviewData;
    const lotObjects = reviewData?.arguments?.objects;

    // set confidence level related state
    const isManuallyModified = lotData?.shippingReviewData?.isLotModified;
    const confidenceLevels = lotData?.shippingReviewData?.confidenceLevels;
    setConfidenceLevelPerField(confidenceLevels);

    // Check overall confidence values: if any value < 0.5 -> 'low', if any value < 0.8 -> 'medium',
    // else -> 'high' used to determine what banner to show in the shipping details modal
    let hasValueBelow05 = false;
    let hasValueBelow08 = false;
    if (confidenceLevels) {
      for (const objectId in confidenceLevels) {
        const objectConfidence = confidenceLevels[objectId];
        if (objectConfidence) {
          for (const fieldName in objectConfidence) {
            const value = objectConfidence[fieldName];
            if (typeof value === 'number') {
              if (value < 0.5) {
                hasValueBelow05 = true;
                break;
              }
              if (value < 0.8) {
                hasValueBelow08 = true;
              }
            }
          }
          if (hasValueBelow05) break;
        }
      }
    }
    if (isManuallyModified || (!hasValueBelow05 && !hasValueBelow08)) {
      setConfidenceLevel('high');
    } else if (hasValueBelow05) {
      setConfidenceLevel('low');
    } else {
      setConfidenceLevel('medium');
    }

    let objectsNeedReview = [];
    if (!!reviewData && lotObjects?.length > 0) {
      setFormData({
      originID: reviewData?.arguments?.originID || '',
      termsID: reviewData?.arguments?.termsID || '',
      items: lotObjects?.map((item) => {
        const hasBelowThreshold = confidenceLevels?.[item.id] && Object.values(confidenceLevels?.[item.id]).some(value => value < 0.8);
        if (hasBelowThreshold && !reviewData.isLotModified) objectsNeedReview.push(item.id);

        return (
          {
            artaSubtype: item.artaSubtype,
            depth: item.depth,
            height: item.height,
            id: item.id,
            isFragile: item.isFragile,
            title: item.title,
            notes: item.notes,
            unitOfMeasurement: item.unitOfMeasurement,
            width: item.width,
          });
        }
      )})
      setExpandedAccordions(prev => new Set(prev).add(lotObjects[0]?.id));
    }
    setExpandedAccordions(prev => new Set([...prev, ...objectsNeedReview]));

    if (!!reviewData && lotObjects.length === 0) {
      setFormData({
        originID: reviewData?.arguments?.originID || '',
        termsID: reviewData?.arguments?.termsID || '',
        items: [{
          artaSubtype: '',
          depth: '',
          height: '',
          id:`new-object-${newObjectsCountRef.current}`,
          isFragile: false,
          notes: '',
          title: '',
          unitOfMeasurement: 'in',
          width: '',
        }]
      });
    }

    // set the SEO friendly url for PDP
    const pdpSeoFriendlyURL = getAuctionLotUrl(lotData?.lotTitle, lotData?.lotNumber, lotData?.lotRef);
    const pdpURL = `https://${isProd ? 'www' : 'stage'}.invaluable.com/auction-lot/${pdpSeoFriendlyURL}`;
    setPDPUrl(pdpURL);
  }, [lotData]);

  useEffect(() => {
    switch (confidenceLevel) {
      case 'low':
        setWarningMessage({message: 'There was an issue generating a shipping quote. Review the shipping details and save.', color: 'color-error'});
        break;
      case 'medium':
        setWarningMessage({message: 'Please review the suggested highlighted area(s) below.', color: 'color-warning'});
        break;
      case 'high':
        setWarningMessage({});
        break;
      default:
        setWarningMessage({});
    }
  }, [confidenceLevel]);

  // Lot with 1 object vs multiple objects
  useEffect(() => {
    const len = formData?.items?.length;
    if (!len || len === 0) return;
    if (len > 0) validateAllValues();
    if (len > 1) {
      setMultiItemFlag(true);
    } else if (len === 1){
      setMultiItemFlag(false);
    }
  }, [formData?.items?.length, validateAllValues]);

  useEffect(() => {
    if (!deletionConfirmed) return;
    if (deletionConfirmed) {
      deleteObject();
      setIndexOfLotToDelete(null);
      setDeletionConfirmed(false);
      setShowDeleteModal(false);
    }
  }, [deletionConfirmed, deleteObject, setIndexOfLotToDelete, setDeletionConfirmed, setShowDeleteModal]);

  // used to set the CTA enabled/disabled
  useEffect(() => {
    let hasAnyError = Object.values(isInvalid).some(nestedItem => {
      return Object.values(nestedItem).includes(true);
    });
    setHasError(hasAnyError);
  }, [isInvalid]);

  // manually edited on the FE, confidenceLevel needs to be set to 1 so we stop showing error/warning
  const adjustConfidenceLevels = (id, fieldName) => {
    if (!fieldsToCompareForConfidenceLevel.includes(fieldName)) return;
    setConfidenceLevelPerField(prev => ({
      ...prev,
      [id]: {
        ...prev?.[id],
        [fieldName]: 1,
      },
    }));
  };

  const handleInputChange = (id, name, value) => {
    // the fields are being manually modified, so confidence level is high
    if (confidenceLevel !== 'high') setConfidenceLevel('high');
    // name is constructed from field name and index of object, e.g. height-0
    const indexOfTarget = name.split('-')[1]; // typeof string
    const nameOfField = indexOfTarget ? name.split('-')[0] : name;
    adjustConfidenceLevels(id, nameOfField);
    if (requiredObjectLevelFields.includes(nameOfField) ) validateSingleValue(id, nameOfField, value);

    // object level fields
    if(indexOfTarget) {
      let newValue = value;
      if (nameOfField === 'isFragile') {
        newValue = value === 'true' ? true : false;
      }
      const newItem = {
        ...formData.items[indexOfTarget],
        [nameOfField]: newValue,
      };
      const newItems = [...formData.items];
      newItems.splice(indexOfTarget, 1, newItem);
      setFormData({
        ...formData,
        items: newItems
      });
    } else {
      // lot level fields
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  const handleFieldFocus = (id, name) => {
    const indexOfTarget = name.split('-')[1]; // typeof string
    const fieldName = indexOfTarget ? name.split('-')[0] : name;
    adjustConfidenceLevels(id, fieldName);
  };

  const lotLevelFieldChangeHandler = (e) => {
    const { name, value } = e.target;
    const indexOfTarget = name.split('-')[1]; // typeof string
    const fieldName = indexOfTarget ? name.split('-')[0] : name;
    handleInputChange(lotData.lotID, fieldName, value);
  };

  const onAddObject = () => {
    /*
      Newly added objects don't have id until saved to the BE. Assign temporary id to them.
    */
    newObjectsCountRef.current = newObjectsCountRef.current + 1;
    const newItemId = `new-object-${newObjectsCountRef.current}`;

    let newItems = formData?.items?.length > 0 ? [...formData.items] : [];
    newItems.push({
      artaSubtype: '',
      depth: '',
      height: '',
      id: newItemId,
      isFragile: false,
      notes: '',
      title: '',
      unitOfMeasurement: 'In',
      width: '',
    });
    setFormData({
      ...formData,
      items: newItems
    });
    setExpandedAccordions(prev => new Set(prev).add(newItemId));
  };

  const onCopyObject = (index) => {
    if (!formData?.items?.length || index === undefined) return;

    const itemToCopy = formData.items[index];
    if (!itemToCopy) return;

    newObjectsCountRef.current = newObjectsCountRef.current + 1;
    const newItemId = `new-object-${newObjectsCountRef.current}`;

    const copiedItem = {
      ...itemToCopy,
      id: newItemId,
    };

    const newItems = [...formData.items, copiedItem];
    setFormData({
      ...formData,
      items: newItems
    });
    setExpandedAccordions(prev => new Set(prev).add(newItemId));
  };

  const onDeleteObject = (index) => {
    setShowDeleteModal(true);
    setIndexOfLotToDelete(index);
  };

  const handleAccordionChange = (itemId) => (event, isExpanded) => {
    setExpandedAccordions(prev => {
      const newSet = new Set(prev);
      if (isExpanded) {
        newSet.add(itemId);
      } else {
        newSet.delete(itemId);
      }
      return newSet;
    });
  };

  const onClose = () => {
    close();
    setFormData({});
    setIsInvalid({});
    setHasError(false);
    setExpandedAccordions(new Set());
    newObjectsCountRef.current = 0;
  }

  const handleSave = () => {
    const oldReviewData = lotData?.shippingReviewData;
    if (!oldReviewData) {
      console.error('No shipping review data found');
      return;
    }
    const newReviewData = createShippingReviewPayloadForUpdate(lotData, formData, false);
    onSave(lotData.lotID, oldReviewData.id, lotData.houseID, newReviewData);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} toggle={onClose} centered size="lg" data-shipping-review-id={lotData?.shippingReviewID}>
      <ModalHeader tag="h4" toggle={onClose}>
        Edit Shipping Details
      </ModalHeader>
      <ModalBody>
        {lotData?.shippingReviewData?.status === 'failed' ? (
          <p className="color-error">There was an issue generating a shipping quote. Review the shipping details and save</p>
        ) : (
          warningMessage?.message && (
            <p className={warningMessage?.color}>{warningMessage?.message}</p>
          )
        )}
        <Form>
          <div className="border rounded p-3 mb-4">
            <a target="_blank" rel="noopener noreferrer" href={pdpUrl} className='text-decoration-none text-reset'>
              <div className="d-flex align-items-center">
                <div className="me-3" style={{ width: '70px', height: '70px' }}>
                  <img
                    src={lotData?.imageUrl}
                    alt="Lot Thumbnail"
                    className="img-fluid"
                    style={{ maxWidth: '100%', maxHeight: '100%' }}
                  />
                </div>
                <div className="p-3">
                  <strong>#{lotData?.lotNumber}: {lotData?.lotTitle}</strong>
                </div>
              </div>
            </a>
          </div>

          <Row className="mb-4">
            <Col md={6}>
              <FormGroup>
                <Label for="fulfillmentLocation" className="fw-bold">
                  Fulfillment Location*
                </Label>
                <Input
                  type="select"
                  name="originID"
                  id="fulfillmentLocation"
                  value={formData?.originID}
                  onChange={lotLevelFieldChangeHandler}
                >
                  {fulfillmentOrigins && fulfillmentOrigins.map(origin => {
                    return (
                      <option key={origin.id} value={origin.id}>{origin.address.addressLine1}</option>
                    );
                  })}
                </Input>
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label for='shippingTerms' className="fw-bold">
                  Shipping Terms*
                </Label>
                <Input
                  type="select"
                  name="termsID"
                  id='shippingTerms'
                  value={formData?.termsID}
                  onChange={lotLevelFieldChangeHandler}
                >
                  {policyTerms && policyTerms.map(term => {
                    return (
                      <option key={term.id} value={term.id}>{term.name}</option>
                    );
                  })}
                </Input>
              </FormGroup>
            </Col>
          </Row>

          {multiItemFlag && formData?.items?.map((item, index) => {
            const isExpanded = expandedAccordions.has(item.id);
            return (
              <Accordion
                key={item.id}
                expanded={isExpanded}
                onChange={handleAccordionChange(item.id)}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls={`panel${index}-content`}
                  id={`panel${index}-header`}
                >
                  <Typography variant='h6'style={{color: item.id.startsWith('new-object') ? '#9EA3A8' : '#333333}'}} className={item.id.startsWith('new-object') ? '' : 'font-weight-bold'}>
                    {`${index+1}. ${item.id.startsWith('new-object-') ? 'Add New Object' : item.title}`}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <EditItemShippingDetails
                    index={index}
                    id={item.id}
                    handleInputChange={handleInputChange}
                    onFieldFocus={handleFieldFocus}
                    formData={formData}
                    isInvalid={isInvalid}
                    artaSubtypeList={artaSubtypeList}
                    deleteObject={onDeleteObject}
                    copyObject={onCopyObject}
                    confidenceScoresPerFields={confidenceLevelPerField?.[item.id]}
                    isManuallyModified={lotData?.shippingReviewData?.isLotModified}
                  />
                </AccordionDetails>
              </Accordion>
            );
          })}
          {!multiItemFlag && formData?.items?.map((item, index) => {
            return (<EditItemShippingDetails
              key={item.id}
              index={index}
              id={item.id}
              handleInputChange={handleInputChange}
              onFieldFocus={handleFieldFocus}
              formData={formData}
              isInvalid={isInvalid}
              artaSubtypeList={artaSubtypeList}
              deleteObject={onDeleteObject}
              copyObject={onCopyObject}
              confidenceScoresPerFields={confidenceLevelPerField?.[item.id]}
              isManuallyModified={lotData?.shippingReviewData?.isLotModified}
              single
            />)
          })}
        </Form>
        <Button color="link" onClick={onAddObject} >
          <FontAwesomeIcon icon={faPlus} />
          <span> Add Object</span>
        </Button>
      </ModalBody>
      <ModalFooter className="justify-content-end border-0">
        <Button color="link" onClick={onClose}>
          Cancel
        </Button>
        <Button
          color="success"
          onClick={handleSave}
          style={{ backgroundColor: '#4D9585', borderColor: '#4D9585' }}
          disabled={hasError}
        >
          Save
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default ShippingDetailsModal;
